import { PbsAdUnit } from '@pubstack/common/src/pbs/adunit'
import { FunctionComponent, useState } from 'react'
import { useAdUnitsStacks } from '~/api/pbs/ad-units.api'
import { Alert } from '~/components/Alert'
import { CodeSnippet } from '~/components/CodeSnippet'
import { Flyout } from '~/components/Flyout'
import { FoldingContent } from '~/components/FoldingContent'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { useToast } from '~/components/Toast/useToasts'
import { useAdFormat } from '~/modules/adm/inventory/adunits/AdFormatDisplay'
import { StackUsage } from '~/modules/adm/StackUsage'
import { StackUsageDetails } from '~/modules/adm/StackUsageDetails'
import {
  ampIntegrationExample,
  androidBannerIntegrationExample,
  androidInstreamIntegrationExample,
  androidIntegrationNativeExample,
  androidInterstitialIntegrationExample,
  iosInstreamIntegrationExample,
  iosIntegrationBannerExample,
  iosIntegrationInterstitialExample,
  iosIntegrationNativeExample,
} from '~/utils/adUnitsConfiguration'

const section = 'flex flex-col gap-3 py-2 font-roboto text-base leading-6.5'
const title = 'font-rubik text-base leading-6.5 font-medium'
/* The snippet's own `code` element sizes to its text, which leaves a short snippet a narrow box. */
const codeSnippet = '[&_code]:w-full'

const getAndroidIntegrationExampleByFormat = (adUnitName: string, adFormatId: string) => {
  const mapping: Record<string, string> = {
    APP_INTERSTITIAL: androidInterstitialIntegrationExample(adUnitName),
    APP_BANNER: androidBannerIntegrationExample(adUnitName),
    APP_INSTREAM: androidInstreamIntegrationExample(adUnitName),
    APP_NATIVE: androidIntegrationNativeExample(adUnitName),
  }
  return mapping[adFormatId]
}

const getIosIntegrationExampleByFormat = (adUnitName: string, adFormatId: string) => {
  const mapping: Record<string, string> = {
    APP_INTERSTITIAL: iosIntegrationInterstitialExample(adUnitName),
    APP_BANNER: iosIntegrationBannerExample(adUnitName),
    APP_INSTREAM: iosInstreamIntegrationExample(adUnitName),
    APP_NATIVE: iosIntegrationNativeExample(adUnitName),
  }
  return mapping[adFormatId]
}

type Props = {
  adUnit: PbsAdUnit
  siteId?: string
}

export const ViewAdUnitFlyout: FunctionComponent<Props> = ({ adUnit, siteId }) => {
  // Hooks
  const toast = useToast()
  const adFormatDisplay = useAdFormat(adUnit.adFormat?.id)
  // Read here rather than handed in by the list: the flyout opens from more than one place, and a usage
  // handed over at open time would stay empty if the list had not loaded it yet.
  const { data: stacksByAdUnit, isLoading: isLoadingUsage } = useAdUnitsStacks()
  const [showUsage, setShowUsage] = useState(false)

  const copiedToClipboardToast = () => {
    toast.success(
      <>
        <strong>{adUnit.name}</strong> set-up code was copied to your clipboard
      </>
    )
  }

  if (showUsage) return <StackUsageDetails name={adUnit.name} stacksUse={stacksByAdUnit[adUnit.id]} onBack={() => setShowUsage(false)} />

  return (
    <Flyout.Content>
      <Flyout.Title>View ad unit</Flyout.Title>
      <Flyout.Body>
        <div className={section}>
          <h2 className={title}>Identification</h2>
          <div className={'flex flex-col gap-0.5'}>
            <h3 className={'font-roboto text-sm leading-5.5 font-normal text-slate-grey'}>Ad unit name</h3>
            {/* Renaming is unwired for now — see the same note in `PureAdmAdUnitDetailsFlyout`. The modal
                stays in the tree; only the way in is gone. */}
            <span className={'truncate'}>{adUnit.name}</span>
          </div>
        </div>
        {adUnit.adFormat && (
          <div className={section}>
            <h2 className={title}>Format</h2>
            <div className={'flex flex-row items-center gap-1'}>
              <Icon name={adFormatDisplay!.iconName} width={'44px'} />
              <span>{adFormatDisplay!.name}</span>
            </div>
          </div>
        )}
        <div className={section}>
          <h2 className={title}>Configuration</h2>

          {adUnit.env === 's2s' && (
            <div className={codeSnippet}>
              <CodeSnippet
                trackingLabel="pbs-gamManager-view"
                contentText={`{
    bidder: 'pubstack',
    params: {${
      siteId
        ? `
      siteId: '${siteId}',`
        : ''
    }
      adUnitName: '${adUnit.name}',
    }
}
`}
                onCodeCopy={copiedToClipboardToast}
              />
            </div>
          )}
          {adUnit.env === 'inapp' && (
            <div className={codeSnippet}>
              <FoldingContent title={'iOS (Swift)'} isFolding={true}>
                <CodeSnippet trackingLabel="pbs-ios-configuration-view" contentText={getIosIntegrationExampleByFormat(adUnit.name, adUnit.adFormat!.id)} onCodeCopy={copiedToClipboardToast} />
              </FoldingContent>

              <FoldingContent title={'Android (Kotlin)'} isFolding={true}>
                <CodeSnippet trackingLabel="pbs-android-configuration-view" contentText={getAndroidIntegrationExampleByFormat(adUnit.name, adUnit.adFormat!.id)} onCodeCopy={copiedToClipboardToast} />
              </FoldingContent>
            </div>
          )}

          {adUnit.env === 'amp' && (
            <>
              <Alert variant="primary" iconName="info">
                <div>
                  Please be sure that the tag settings match your ad inventory. Need specific setup? Visit the{' '}
                  <Link
                    label={'Pubstack Help Center'}
                    href={`https://docs.pubstack.io/monetization-manager-node/introduction/amp-environment-technical-requirements`}
                    trackingLabel={`pbs-pubstack-help-center-navigate`}
                    variant="secondary"
                    target="_blank"
                  />
                  .
                </div>
              </Alert>
              {!siteId && (
                <Alert variant="warning" iconName="warning">
                  <div>Don't forget to specify the Pubstack Site ID on the snippet code.</div>
                </Alert>
              )}
              <div className={codeSnippet}>
                <CodeSnippet trackingLabel="pbs-gamManager-view" contentText={ampIntegrationExample(adUnit.name, siteId)} onCodeCopy={copiedToClipboardToast} />
              </div>
            </>
          )}
        </div>
        {/* Last, as in every other view flyout. */}
        {!isLoadingUsage && <StackUsage className={'py-2'} stacksUse={stacksByAdUnit[adUnit.id]} onSeeAll={() => setShowUsage(true)} />}
      </Flyout.Body>
    </Flyout.Content>
  )
}
